// ---------------------------------------------------------------------------
// Felton Chart DNA -- Animation Timing Constants
// ---------------------------------------------------------------------------
// Centralized animation timing values matching the CSS custom properties
// defined in index.css (--felton-chart-duration, --felton-chart-easing).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChartAnimationConfig {
  /** Base bar animation duration in ms */
  readonly duration: number
  /** Bounce easing curve for bar fill */
  readonly easing: string
  /** Stagger delay between consecutive bars in ms */
  readonly staggerDelay: number
  /** Duration when prefers-reduced-motion is active */
  readonly reducedMotionDuration: number
  /** Donut chart arc animation duration in ms */
  readonly donutDuration: number
  /** Gauge chart arc animation duration in ms */
  readonly gaugeDuration: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CHART_ANIMATION: ChartAnimationConfig = Object.freeze({
  duration: 700,
  easing: 'cubic-bezier(0.34, 1, 0.64, 1)',
  staggerDelay: 80,
  reducedMotionDuration: 0,
  donutDuration: 900,
  gaugeDuration: 800,
})

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Computes the stagger delay for the n-th bar in a cascade animation.
 * @param index - The zero-based bar index
 * @param stagger - Override stagger interval (default: CHART_ANIMATION.staggerDelay)
 */
export function getStaggerDelay(
  index: number,
  stagger: number = CHART_ANIMATION.staggerDelay,
): number {
  return index * stagger
}

/**
 * Returns the correct animation duration based on reduced-motion preference.
 * @param prefersReduced - Whether the user prefers reduced motion
 * @param baseDuration - Override base duration (default: CHART_ANIMATION.duration)
 */
export function getReducedMotionDuration(
  prefersReduced: boolean,
  baseDuration: number = CHART_ANIMATION.duration,
): number {
  return prefersReduced ? CHART_ANIMATION.reducedMotionDuration : baseDuration
}
